import React, {useEffect, useState} from 'react';
import {Link,useLocation} from 'react-router-dom';
import {type Category, getCategoryList} from '@/api/api';
import styles from '@/styles/global.module.css';

interface SidebarProps {
    mainCategory: string;
    activeSubcategory?: string;
}

export default function Sidebar({mainCategory, activeSubcategory}: SidebarProps) {
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const location = useLocation();
    const pathname=location.pathname;

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setLoading(true);
                const response = await getCategoryList();
                if (response.data.code === 1) {
                    setCategories(response.data.data);
                }
            } catch (error) {
                console.error('Error fetching categories:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, []);

    // Generate URL for a subcategory
    const getSubcategoryUrl = (category: string, label: string) => {
        const categoryMap: { [key: string]: string } = {
            '新闻中心': 'news',
            '研究动态': 'research',
            '政策法规': 'policies',
            '职业技能': 'skills'
        };

        const labelMap: { [key: string]: string } = {
            '通知公告': 'notices',
            '决策参考': 'decisions',
            '调查研究': 'research',
            '政策解读': 'policy',
            '教育动态': 'education'
        };

        const basePath = categoryMap[category] || category.toLowerCase();
        const labelPath = labelMap[label] || label.toLowerCase();

        return `/${basePath}/${labelPath}`;
    };

    // Render subcategory links based on the main category and API data
    const renderSubcategoryLinks = () => {
        if (loading) return null;

        // Find the current category in the API data
        const currentCategory = categories.find(cat => cat.name === mainCategory);

        if (currentCategory && currentCategory.labelList && currentCategory.labelList.length > 0) {
            return (
                <>
                    {currentCategory.labelList.map(label => {
                        const url = getSubcategoryUrl(currentCategory.name, label);
                        return (
                            <li key={label} className={activeSubcategory === label ? styles.active : ''}>
                                <Link to={url} className={pathname === url ? styles.active : ''}>
                                    {label}
                                </Link>
                            </li>
                        );
                    })}
                </>
            );
        }

        // Fallback to static subcategories if API doesn't have them
        if (mainCategory === '研究动态') {
            return (
                <>
                    <li className={activeSubcategory === '政策解读' ? styles.active : ''}>
                        <Link to="/research/policy"
                              className={pathname === '/research/policy' ? styles.active : ''}>政策解读</Link>
                    </li>
                    <li className={activeSubcategory === '教育动态' ? styles.active : ''}>
                        <Link to="/research/education"
                              className={pathname === '/research/education' ? styles.active : ''}>教育动态</Link>
                    </li>
                </>
            );
        } else if (mainCategory === '新闻中心') {
            return (
                <>
                    <li className={activeSubcategory === '通知公告' ? styles.active : ''}>
                        <Link to="/news/notices"
                              className={pathname === '/news/notices' ? styles.active : ''}>通知公告</Link>
                    </li>
                    <li className={activeSubcategory === '决策参考' ? styles.active : ''}>
                        <Link to="/news/decisions"
                              className={pathname === '/news/decisions' ? styles.active : ''}>决策参考</Link>
                    </li>
                    <li className={activeSubcategory === '调查研究' ? styles.active : ''}>
                        <Link to="/news/research"
                              className={pathname === '/news/research' ? styles.active : ''}>调查研究</Link>
                    </li>
                </>
            );
        }

        return null;
    };

    return (
        <div className={styles.pageLeft}>
            <div className={styles.fllist}>
                <ul>
                    <li className={mainCategory === '关于我们' ? styles.active : ""}>
                        <Link to="/about"
                              className={pathname === '/about' ? styles.active : ""}>关于我们</Link>
                    </li>
                    <li className={mainCategory === '研究动态' ? styles.active : ""}>
                        <Link to="/research"
                              className={pathname.startsWith('/research') ? styles.active : ""}>研究动态</Link>
                    </li>
                    <li className={mainCategory === '新闻中心' ? styles.active : ""}>
                        <Link to="/news" className={pathname.startsWith('/news') ? styles.active : ""}>新闻中心</Link>
                    </li>
                    <li className={mainCategory === '政策法规' ? styles.active : ""}>
                        <Link to="/policies"
                              className={pathname.startsWith('/policies') ? styles.active : ""}>政策法规</Link>
                    </li>
                    <li className={mainCategory === '职业技能' ? styles.active : ""}>
                        <Link to="/skills"
                              className={pathname.startsWith('/skills') ? styles.active : ""}>职业技能</Link>
                    </li>
                    <li className={mainCategory === '证书查询' ? styles.active : ""}>
                        <Link to="/certificates"
                              className={pathname.startsWith('/certificates') ? styles.active : ""}>证书查询</Link>
                    </li>
                    <li className={mainCategory === '联系我们' ? styles.active : ""}>
                        <Link to="/contact"
                              className={pathname === '/contact' ? styles.active : ""}>联系我们</Link>
                    </li>
                </ul>
            </div>
        </div>
    );
} 